import React, { useRef, useEffect } from 'react';
import { Box, TextField, Typography } from '@mui/material';

/**
 * OTP component that handles multi-input fields, each holding one digit
 * 
 * @param {string} [props.label] - An optional title for the group of inputs.
 * @param {number} [props.length=6] - The number of input boxes (default is 6).
 * @param {string[]} [props.value=[]] - An array holding the current values for each input.
 * @param {Function} props.onChange - Callback fired when one of the inputs changes,
 *  receiving the updated OTP array as an argument.
 *
 */

const OtpInput = ({ label, length = 6, value = [], onChange }) => {
  const inputRefs = useRef([]);

  // Focus the first input box automatically on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Handles changes in each input field to update the otp value
  const handleChange = (index, event) => {
    const inputValue = event.target.value;

    // validation to only allow digits
    if (!/^\d*$/.test(inputValue)) return;

    const newOtp = [...value];
    newOtp[index] = inputValue.slice(-1);
    onChange(newOtp);

    // If user typed something (not backspace), focus the next box
    if (inputValue && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  // When user presses backspace in an empty box, focus on previous box
  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !value[index] && index > 0) {
      const newOtp = [...value];
      newOtp[index - 1] = '';
      onChange(newOtp);
      inputRefs.current[index - 1].focus();
    }
  };

  return (
    <Box>
      {/* optional label for input fields */}
      {label && (
        <Typography
          variant="body2"
          sx={{
            textAlign: 'left',
            color: 'var(--navyBlue-default)',
            fontWeight: '500',
            marginBottom: 0.5,
          }}
        >
          {label}
        </Typography>
      )}

      {/* otp input fields,
      Each input is tied to an index and references the same array in value */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        {Array.from({ length }, (_, index) => (
          <TextField
            key={index}
            value={value[index] || ''}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            inputRef={(el) => (inputRefs.current[index] = el)}
            variant="outlined"
            sx={{
              width: '53px',
              height: '53px',
              textAlign: 'center',
              '& .MuiInputBase-input': {
                textAlign: 'center',
                fontSize: '1.25rem',
                fontWeight: '500',
              },
              '& .MuiOutlinedInput-root': {
                borderColor: 'var(--gray-300)',
                '&:hover fieldset': {
                  borderColor: 'var(--gray-300) !important',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--navyBlue-default) !important',
                },
              },
            }}
            inputProps={{
              maxLength: 1,
            }}
            required
          />
        ))}
      </Box>
    </Box>
  );
};

export default OtpInput;
